import React from 'react';
import { useSelector } from 'react-redux';
import uniqueId from 'lodash/uniqueId';
import { NextPage, NextPageContext } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import { tariffs } from '@r1-frontend/api-domru/api-content/v1/cities/tariffs';
import {
    TResponseDto as TTariffsResponseDto,
} from '@r1-frontend/api-domru/api-content/v1/cities/tariffs/dto/ResponseDto';
import constructorPrice from '@r1-frontend/api-domru/api-tariffs/v1/tariff/constructor-price';
import { ResponseDto } from '@r1-frontend/api-domru/api-tariffs/v1/tariff/constructor-price/dto/ResponseDto';
import getPackageLines, { ResponseDto as PackageLinesResponseDto } from '@r1-frontend/api-domru/api-tariffs/v1/web/lines';

import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { getBanners, groupByTags } from '~/src/api/content/banners';
import { getPageView } from '~/src/api/content/page/view';
import { EExpandShopItems, getShopItems } from '~/src/api/content/shop/getShopItems';
import { createTariffBuilder } from '~/src/components/Bundles/helpers';
import { ITariff } from '~/src/entities/formattedTariff';
import Layout from '~/src/features/_layout';
import { SeoScriptAggregateRating, SeoScriptAggregateRatingWithOffer } from '~/src/features/_layout/SeoScripts';
import { ISeoScriptAggregateRating } from '~/src/features/_layout/SeoScripts/SeoScriptAggregateRating';
import { TPropsWithTariffCardsRedesignData } from '~/src/features/index';
import { FIRST_BANNER_TOP_SALE } from '~/src/features/index/constants/abnVariants';
import { TARIFF_CARDS_REDESIGN } from '~/src/features/index/MultiPackageList/tariffCardRedesignAbnTest';
import transformApiTariffAnswerToMultiPackage from '~/src/features/index/MultiPackageList/transformApiTariffAnswerToMultiPackage';
import { getABNVariantOnSSr } from '~/src/helpers/abn-tests';
import { initialProps } from '~/src/helpers/basePageFunc';
import { TARIFF_BETWEEN_BANNERS } from '~/src/helpers/city';
import { getCookie } from '~/src/helpers/cookie';
import getRequester from '~/src/helpers/getRequester';
import { saveTariffs } from '~/src/store/actions/bundles';
import { setTariffsForInternet } from '~/src/store/actions/internet';
import { getCurrentCityIn, selectCompanyName } from '~/src/store/selectors/city';
import { selectMinCostedTariff } from '~/src/store/selectors/filteredBundles';
import selectProviderId from '~/src/store/selectors/selectProviderId';

const Index = dynamic(() => import('~/src/features/index'), { ssr: true });

const topSalesID = '789465';
const filter = {
    'filter[is_sale]': 1,
};

const IndexPage: NextPage<TPropsWithTariffCardsRedesignData> = (props) => {
    const companyName = useSelector(selectCompanyName);
    const cityIn = useSelector(getCurrentCityIn);
    const minCostedTariff = useSelector(selectMinCostedTariff((tariff: ITariff) => tariff.availableOnMainPage));

    const baseSeoData: ISeoScriptAggregateRating = {
        name: `Провайдер ${companyName} в ${cityIn}`,
        ratingCount: '1898',
        ratingValue: '4.8',
    };

    return (
        <>
            <Head>
                <meta name='robots' content='noyaca'/>
            </Head>
            {minCostedTariff
                ? <SeoScriptAggregateRatingWithOffer
                    {...minCostedTariff}
                    {...baseSeoData}
                />
                : <SeoScriptAggregateRating {...baseSeoData}/>
            }
            <Layout
                title={`Провайдер интернета ${companyName} в ${cityIn}`}
                description={`Официальный сайт провайдера ${companyName} в ${cityIn}. Подключить быстрый Интернет, Цифровое телевидение и онлайн-кинотеатр, Домофон и Видеонаблюдение. Адреса и телефоны офисов.`}
                withMenu={true}
                withFooter={true}
                withChat={true}
                bgColor={COLORS.BgMain}
            >
                <Index {...props}/>
            </Layout>
        </>
    );
};

IndexPage.getInitialProps = async(options: NextPageContext): Promise<TPropsWithTariffCardsRedesignData> => {
    await initialProps(options, { isNeedAuthData: true });
    const { req, store } = options;

    const state = store.getState();

    const providerId = Number(getCookie('providerId', req) || selectProviderId(state));

    const isTariffsBetweenBanners = getABNVariantOnSSr(TARIFF_BETWEEN_BANNERS.testName, req) === TARIFF_BETWEEN_BANNERS.variants.ENABLED;
    const isFirstBannerTopSaleAbTestActive = getABNVariantOnSSr(FIRST_BANNER_TOP_SALE.testName, req) === FIRST_BANNER_TOP_SALE.variants.on;
    const isRedesignedTariffCards = getABNVariantOnSSr(TARIFF_CARDS_REDESIGN.testName, req) === TARIFF_CARDS_REDESIGN.variants.NEW;

    const bannerTags = ['tariff', 'marketing', 'custom', 'main_promo_double', 'main_promo'];

    if (isFirstBannerTopSaleAbTestActive) {
        bannerTags.push('first_banner_top_sale');
    }

    const doRequest = getRequester(options.req);

    if (isRedesignedTariffCards) {
        const [
            pageViewResponse,
            shopItemsResponse,
            bannersResponse,
            tariffsResponse,
        ] = await Promise.all([
            getPageView(providerId, topSalesID),
            getShopItems(
                providerId,
                null,
                null,
                [EExpandShopItems.Category, EExpandShopItems.Labels, EExpandShopItems.Props],
                filter,
            ),
            getBanners({ providerId, tag: bannerTags, expand: 'bannerInfo.advertising,bannerInfo.tags,order' }),
            doRequest.apiTariffs<PackageLinesResponseDto>(getPackageLines()),
        ]);

        const topSalesBlocksInfo = pageViewResponse.isSuccess ? pageViewResponse?.payload : undefined;
        const banners = bannersResponse.isSuccess ? groupByTags(bannersResponse.payload, bannerTags, []) : {};

        return {
            productList: shopItemsResponse,
            topSalesBlocksInfo,
            banners,
            isTariffsBetweenBanners,
            isRedesignedTariffCards: true,
            packageLines: tariffsResponse.isSuccess ? transformApiTariffAnswerToMultiPackage(tariffsResponse.payload) : {},
        };
    }

    const [
        pageViewResponse,
        shopItemsResponse,
        bannersResponse,
        constructorPriceResponse,
        tariffsResponse,
    ] = await Promise.all([
        getPageView(providerId, topSalesID),
        getShopItems(
            providerId,
            null,
            null,
            [EExpandShopItems.Category, EExpandShopItems.Labels, EExpandShopItems.Props],
            filter,
        ),
        getBanners({ providerId, tag: bannerTags, expand: 'bannerInfo.advertising,bannerInfo.tags,order' }),
        doRequest.apiTariffs<ResponseDto>(constructorPrice(providerId)),
        doRequest.apiContent<TTariffsResponseDto>({
            ...tariffs({ providerId }),
            withProvider: false,
        }),
    ]);

    if (constructorPriceResponse?.isSuccess && tariffsResponse.isSuccess) {
        const mono = [
            ...tariffsResponse.payload.mono,
            createTariffBuilder(constructorPriceResponse.payload.mono),
        ];

        const bundle = {
            ...tariffsResponse.payload.bundles,
            [uniqueId()]: createTariffBuilder(constructorPriceResponse.payload.bundle),
        };

        store.dispatch(setTariffsForInternet(mono));
        store.dispatch(saveTariffs(bundle));
    }

    if (!constructorPriceResponse?.isSuccess && tariffsResponse.isSuccess) {
        store.dispatch(setTariffsForInternet(tariffsResponse.payload.mono));
        store.dispatch(saveTariffs(tariffsResponse.payload.bundles));
    }

    const topSalesBlocksInfo = pageViewResponse.isSuccess ? pageViewResponse?.payload : undefined;
    const banners = bannersResponse.isSuccess ? groupByTags(bannersResponse.payload, bannerTags, []) : {};

    return {
        productList: shopItemsResponse,
        topSalesBlocksInfo,
        banners,
        isTariffsBetweenBanners,
        isRedesignedTariffCards: false,
    };
};

export default IndexPage;
